import { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { showSuccessToastFullScreen, showErrorToastFullScreen } from '../utils/toastUtils';
import { captureEvent } from '../services/analytics';
import { takeGmailReturnTo } from '../utils/gmailReturn';

/**
 * Handles the Gmail OAuth callback (`?gmail=connected` / `?gmail=error`) that the
 * backend appends when it redirects the browser back into the app.
 *
 * The backend always lands on /settings, so if the user started the connect
 * elsewhere we replay their recorded origin (carrying the param along) and let
 * the destination's copy of this hook show the toast — the stored origin is
 * consumed on read, so there is no relay loop. When this page _is_ the origin
 * (or none was recorded), we show the success/error toast here, fire analytics,
 * and strip the param from the URL.
 *
 * @param onResult optional page-specific side effect once the result lands here
 *                 (e.g. refresh Gmail connection status). Not called on the relay
 *                 hop, only on the page that actually shows the toast.
 */
export function useGmailConnectResult(
  onResult?: (status: 'connected' | 'error') => void,
): void {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  // Process the callback once per page instance. Guards against StrictMode's
  // dev-only double-invoke (the ref survives it) firing the toast/relay twice.
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;

    const status = searchParams.get('gmail');
    if (status !== 'connected' && status !== 'error') return;
    handled.current = true;

    // Relay back to wherever the user started, if that isn't here.
    const returnTo = takeGmailReturnTo();
    if (returnTo && returnTo !== location.pathname) {
      navigate(`${returnTo}?gmail=${status}`, { replace: true });
      return;
    }

    if (status === 'connected') {
      showSuccessToastFullScreen('Gmail connected successfully!');
      captureEvent('gmail_connected', {});
    } else {
      showErrorToastFullScreen('Failed to connect Gmail. Please try again.');
    }

    // Build a fresh instance — mutating the one react-router returned corrupts
    // its internal state and the navigation silently no-ops after a relay.
    const next = new URLSearchParams(searchParams);
    next.delete('gmail');
    setSearchParams(next, { replace: true });

    onResult?.(status);
    // Run-once on mount, so the missing deps stay missing. Every branch below
    // the guard is a side effect that must happen at most once per page
    // instance — a toast, a captureEvent, a history replace, a relay navigate
    // — and `setSearchParams` is what makes `searchParams` change, so listing
    // it would re-enter this effect from its own last statement. (`handled`
    // would swallow the re-entry; the point is not to build the loop.)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
